import { supabase } from '@/integrations/supabase/client';

export interface ChildSpendingStatus {
  currentSpentCents: number;
  limitCents: number;
  remainingCents: number;
  isAtLimit: boolean;
  percentageUsed: number;
}

export interface PurchaseCheckResult {
  allowed: boolean;
  reason?: string;
  currentSpentCents: number;
  limitCents: number;
  remainingCents: number;
}

const SPENDING_LIMIT_CENTS = 5000; // $50.00

/**
 * Check if a user's age group indicates they are under 13
 */
export const isAgeGroupUnder13 = (ageGroup: string | null | undefined): boolean => {
  if (!ageGroup) return false;
  
  const ageStr = ageGroup.toLowerCase();
  
  // Check for explicit under-13 age groups
  if (ageStr === '13-and-under' || ageStr === 'under-13' || ageStr === 'under13') {
    return true;
  }
  
  // Check for numeric ages 1-13
  const numericAge = parseInt(ageGroup);
  if (!isNaN(numericAge) && numericAge >= 1 && numericAge <= 13) {
    return true;
  }
  
  return false;
};

/**
 * Get the spending status for an under-13 user
 */
export const getChildSpendingStatus = async (
  userId: string, 
  userType: 'supabase' | 'internal'
): Promise<ChildSpendingStatus | null> => {
  try {
    // Call the database function to get or create spending limit record
    const { data, error } = await supabase.rpc('get_or_create_child_spending_limit', {
      p_user_id: userId,
      p_user_type: userType
    });

    if (error) {
      console.error('Error fetching child spending status:', error);
      return null;
    }

    if (!data) {
      // Return default values if no record exists
      return {
        currentSpentCents: 0,
        limitCents: SPENDING_LIMIT_CENTS,
        remainingCents: SPENDING_LIMIT_CENTS,
        isAtLimit: false,
        percentageUsed: 0
      };
    }

    const currentSpentCents = data.current_spent_cents || 0;
    const limitCents = data.spending_limit_cents || SPENDING_LIMIT_CENTS;
    const remainingCents = Math.max(0, limitCents - currentSpentCents);

    return {
      currentSpentCents,
      limitCents,
      remainingCents,
      isAtLimit: remainingCents <= 0,
      percentageUsed: Math.min(100, Math.round((currentSpentCents / limitCents) * 100))
    };
  } catch (err) {
    console.error('Error in getChildSpendingStatus:', err);
    return null;
  }
};

/**
 * Check if a child can make a purchase of a given amount
 */
export const canChildPurchase = async (
  userId: string,
  userType: 'supabase' | 'internal',
  amountCents: number
): Promise<PurchaseCheckResult> => {
  try {
    const { data, error } = await supabase.rpc('can_child_purchase', {
      p_user_id: userId,
      p_user_type: userType,
      p_amount_cents: amountCents
    });

    if (error) {
      console.error('Error checking child purchase eligibility:', error);
      return {
        allowed: false,
        reason: 'Unable to verify spending limit. Please try again.',
        currentSpentCents: 0,
        limitCents: SPENDING_LIMIT_CENTS,
        remainingCents: 0
      };
    }

    // Cast data to expected shape
    const result = data as unknown as {
      allowed: boolean;
      reason?: string;
      current_spent_cents: number;
      limit_cents: number;
      remaining_cents: number;
    };

    return {
      allowed: result.allowed,
      reason: result.reason,
      currentSpentCents: result.current_spent_cents,
      limitCents: result.limit_cents,
      remainingCents: result.remaining_cents
    };
  } catch (err) {
    console.error('Error in canChildPurchase:', err);
    return {
      allowed: false,
      reason: 'Unable to verify spending limit. Please try again.',
      currentSpentCents: 0,
      limitCents: SPENDING_LIMIT_CENTS,
      remainingCents: 0
    };
  }
};

/**
 * Format cents to dollars string
 */
export const formatCentsToDollars = (cents: number): string => {
  return `$${(cents / 100).toFixed(2)}`;
};

/**
 * Get the total amount a user has spent from purchase history (for initialization/sync)
 */
export const calculateTotalSpentFromHistory = async (
  userId: string,
  userType: 'supabase' | 'internal'
): Promise<number> => {
  try {
    const { data, error } = await supabase
      .from('points_purchases')
      .select('amount_cents')
      .eq('user_id', userId)
      .eq('user_type', userType)
      .eq('status', 'completed');

    if (error) {
      console.error('Error calculating spent from history:', error);
      return 0;
    }

    return data?.reduce((total, purchase) => total + (purchase.amount_cents || 0), 0) || 0;
  } catch (err) {
    console.error('Error in calculateTotalSpentFromHistory:', err);
    return 0;
  }
};
